import { ExchangeCode } from "@/lib/market-data/market-data.types";
import {
  CandleFetchParams,
  CandleInterval,
  CandlePoint,
  CandleUpdateCallback,
  CandleErrorCallback,
} from "./candle.types";
import { BROWSER_DIRECT_CANDLE_REST_ADAPTERS } from "./adapters/rest";
import { CANDLE_WS_ADAPTERS } from "./adapters/ws";
import { createApiCandlesRestAdapter } from "./adapters/rest/api-candles.rest";
import { requiresApiCandleFallback } from "./exchange-candle-routing";

export interface LiveCandleService {
  fetchCandles(
    exchange: ExchangeCode,
    params: CandleFetchParams
  ): Promise<CandlePoint[]>;
  subscribeCandles(
    exchange: ExchangeCode,
    symbol: string,
    interval: CandleInterval,
    onUpdate: CandleUpdateCallback,
    onError?: CandleErrorCallback
  ): () => void;
}

class DefaultLiveCandleService implements LiveCandleService {
  async fetchCandles(
    exchange: ExchangeCode,
    params: CandleFetchParams
  ): Promise<CandlePoint[]> {
    if (requiresApiCandleFallback(exchange)) {
      return createApiCandlesRestAdapter(exchange).fetchCandles(params);
    }

    const adapter = BROWSER_DIRECT_CANDLE_REST_ADAPTERS[exchange];
    if (!adapter) {
      return createApiCandlesRestAdapter(exchange).fetchCandles(params);
    }

    try {
      return await adapter.fetchCandles(params);
    } catch {
      return createApiCandlesRestAdapter(exchange).fetchCandles(params);
    }
  }

  subscribeCandles(
    exchange: ExchangeCode,
    symbol: string,
    interval: CandleInterval,
    onUpdate: CandleUpdateCallback,
    onError?: CandleErrorCallback
  ): () => void {
    return CANDLE_WS_ADAPTERS[exchange].subscribe(
      symbol,
      interval,
      onUpdate,
      onError
    );
  }
}

let singleton: LiveCandleService | null = null;

export function getLiveCandleService(): LiveCandleService {
  if (!singleton) {
    singleton = new DefaultLiveCandleService();
  }
  return singleton;
}
